import clsx from "clsx";
import * as React from "react";
import { FloatingButton } from "../floating-button/FloatingButton.js";

interface BottomNavProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  classNames?: {
    root?: string;
    content?: string;
  };
  withFloatingButton?: {
    icon: React.ReactNode;
    onFloatingClick?: React.MouseEventHandler<HTMLButtonElement>;
    size?: "sm" | "md";
  };
}

const BottomNav = React.forwardRef<HTMLDivElement, BottomNavProps>(
  (props, ref) => {
    const { className, children, classNames, withFloatingButton, ...rest } =
      props;

    const handleFloatingClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (withFloatingButton?.onFloatingClick) {
          withFloatingButton?.onFloatingClick?.(event);
        }
      },
      [withFloatingButton?.onFloatingClick],
    );

    const childArray = React.Children.toArray(children);
    const childCount = childArray.length;
    const renderFloatingButton =
      !!withFloatingButton && (childCount === 2 || childCount === 4);

    const leftChildren: React.ReactNode[] = [];
    const rightChildren: React.ReactNode[] = [];

    if (childCount === 2) {
      leftChildren.push(childArray[0]);
      rightChildren.push(childArray[1]);
    } else if (childCount === 4) {
      leftChildren.push(childArray[0], childArray[1]);
      rightChildren.push(childArray[2], childArray[3]);
    }

    return (
      <div
        ref={ref}
        className={clsx("ox-reset ox-BottomNav", className, classNames?.root)}
        {...rest}
      >
        <div
          className={clsx("ox-reset ox-BottomNavContent", classNames?.content)}
          style={{
            display: "grid",
            gridTemplateColumns: renderFloatingButton
              ? childCount === 2
                ? "1fr auto 1fr"
                : "1fr 1fr auto 1fr 1fr"
              : `repeat(${childCount}, 1fr)`,
          }}
        >
          {renderFloatingButton ? (
            <>
              {leftChildren}
              <FloatingButton
                className={clsx("ox-reset ox-FloatingActionButton")}
                onClick={handleFloatingClick}
                size={withFloatingButton?.size || "md"}
                icon={withFloatingButton?.icon}
              />
              {rightChildren}
            </>
          ) : (
            children
          )}
        </div>
      </div>
    );
  },
);

BottomNav.displayName = "BottomNav";
export { BottomNav };
export type { BottomNavProps };

type BottomNavLinkProps<C extends React.ElementType> = {
  component?: C;
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
} & React.ComponentProps<C>;

const BottomNavLink = <C extends React.ElementType = "a">({
  icon,
  label,
  component: Component = "a",
  isActive = false,
  className,
  ...props
}: BottomNavLinkProps<C>) => {
  return (
    <Component
      {...(props as React.ComponentProps<typeof Component>)}
      className={clsx("ox-reset ox-BottomNavLinkRoot", className)}
      data-is-active={isActive}
    >
      <span
        className={clsx("ox-reset ox-BottomNavLinkContent")}
        data-is-active={isActive}
      >
        <span className={clsx("ox-reset ox-BottomNavLinkIcon")}>{icon}</span>
        <span className={clsx("ox-reset ox-BottomNavLinkLabel")}>{label}</span>
      </span>
    </Component>
  );
};

BottomNavLink.displayName = "BottomNavLink";
export { BottomNavLink };
export type { BottomNavLinkProps };
